/**
 * Tenant-aware IAM API. Currently exposes the per-org sign-in config —
 * Cognito pool/client IDs etc. — that the frontend uses to bootstrap
 * Amplify. The full IAM surface (users, roles, sessions) lands as more
 * endpoints are wired in later phases.
 */
import axios from 'axios';
import { attachActivityInterceptors } from '@/api/requestActivity';

export interface TenantSignInConfig {
  orgId: string;
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId?: string;
  /** Optional MFA hints, etc. */
  features?: {
    mfaEnabled?: boolean;
    forceMfa?: boolean;
  };
}

// Wire shape returned by `/iam-svc/tenant/{orgId}/sign-in-config`. The
// backend uses PascalCase Cognito-native names; the rest of the frontend
// stays in camelCase, so we normalize at this boundary. Discovered during
// Wave 3.1: prior to this mapping, `configureAmplify` got undefined
// userPoolId/userPoolClientId and silently failed to configure Amplify,
// making sign-in impossible via the v2 web app.
interface TenantSignInConfigWire {
  Region: string;
  UserPoolId: string;
  AppClientId: string;
  CognitoIdentityPoolId?: string;
  Features?: {
    MfaEnabled?: boolean;
    ForceMfa?: boolean;
  };
}

/**
 * Bare axios instance — sign-in-config is unauthenticated by definition
 * (the user has no session yet). Don't use the shared httpClient here
 * because that adds the auth interceptor.
 */
const iamPublic = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 15_000,
});

// Surface the unauthenticated sign-in-config lookup on /welcome in the global
// top progress bar too, using the same shared instrumentation as httpClient.
attachActivityInterceptors(iamPublic);

export async function fetchTenantSignInConfig(orgId: string): Promise<TenantSignInConfig> {
  if (!orgId) throw new Error('orgId is required');
  const { data } = await iamPublic.get<TenantSignInConfigWire>(
    `/iam-svc/tenant/${orgId}/sign-in-config`,
  );
  const features: TenantSignInConfig['features'] = {};
  if (data.Features?.MfaEnabled !== undefined) features.mfaEnabled = data.Features.MfaEnabled;
  if (data.Features?.ForceMfa !== undefined) features.forceMfa = data.Features.ForceMfa;
  return {
    orgId,
    region: data.Region,
    userPoolId: data.UserPoolId,
    userPoolClientId: data.AppClientId,
    ...(data.CognitoIdentityPoolId ? { identityPoolId: data.CognitoIdentityPoolId } : {}),
    ...(Object.keys(features).length > 0 ? { features } : {}),
  };
}
